import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZES,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_LINE_HEIGHT_OPTIONS,
} from 'components/editor/grid/reduxStore/defaultTextDefaultValue';
import type { TableRowDefaultTagSelectors, TableRowStyleType } from 'components/editor/grid/reduxStore/tableRowDefaultStyleSlice';
import type {
  AllTextDefaultStylesType,
  TableTextDefaultTagSelectors,
  TextDefaultTagSelectors,
} from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import ColorPicker from 'components/editor/shared/components/ColorPicker';
import { useAllAvailableFonts } from 'hooks/useAllAvailableFonts';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDocumentColors } from 'components/editor/grid/reduxStore/selectors/documentColorsSelector';

import StyledComponentInput from '../component/StyledComponentInput';
import FontSizeInput from '../component/FontSizeInput';
import TextAlignmentButtons from '../component/TextAlignmentButtons';
import TextStyleButtons from '../component/TextStyleButtons';
import { tableStyleToRowClassMap } from './combineDefaultStyles';
import type { colorParams } from 'components/editor/shared/components/ColorPicker/types';

const fontSizeOptions = DEFAULT_FONT_SIZES.map((option) => option.name);
const lineHeightMenuItems = DEFAULT_LINE_HEIGHT_OPTIONS.map(({ name }) => name);
const lineHeightMenuItemsVal = Object.fromEntries(DEFAULT_LINE_HEIGHT_OPTIONS.map(({ name, level }) => [name, level]));

type UpdateTextDefaultStylingPayloadType = {
  type: TextDefaultTagSelectors | TableTextDefaultTagSelectors;
  style: Partial<AllTextDefaultStylesType>;
};

type UpdateRowDefaultStylingPayloadType = {
  type: TableRowDefaultTagSelectors;
  style: Partial<TableRowStyleType>;
};

export type AccordionItem = { componentTitle: string; components: React.ReactNode };

export type AccordionData = {
  title: string;
  textStyles: AccordionItem[];
  rowStyles: AccordionItem[];
};

export type defaultStyles = {
  title: string;
  textStyles: AllTextDefaultStylesType;
  rowStyles?: TableRowStyleType;
};
interface UseAccordionDataProps {
  defaultStyles: defaultStyles[];
  handleTextDefaultStyleUpdate: (payload: UpdateTextDefaultStylingPayloadType) => void;
  handleRowStyleUpdate?: (payload: UpdateRowDefaultStylingPayloadType) => void;
}

const useAccordionData = ({ defaultStyles, handleTextDefaultStyleUpdate, handleRowStyleUpdate }: UseAccordionDataProps) => {
  const { t } = useTranslation();
  const contentId = useAppSelector(selectContentId);
  const allAvailableFontObjects = useAllAvailableFonts(contentId);
  const allAvailableFonts = useMemo(() => allAvailableFontObjects.map((f) => f.name), [allAvailableFontObjects]);
  const [documentColors, setDocumentColors] = useState<colorParams[] | undefined>(undefined);
  const handleColorPickerOpen = () => {
    setDocumentColors(getDocumentColors());
  };

  const handleTextDefaultStyleChange = (type: TextDefaultTagSelectors, styleToUpdate: string, value: string) =>
    handleTextDefaultStyleUpdate({ type, style: { [styleToUpdate]: value } });

  const handleTableRowDefaultStyleChange = (selector: TableRowDefaultTagSelectors, styleToUpdate: string, value: string) => {
    if (!handleRowStyleUpdate) return;
    handleRowStyleUpdate({ type: selector, style: { [styleToUpdate]: value } });
  };

  const accordionData = Object.keys(defaultStyles).map((value) => {
    const selector = defaultStyles[value].title;
    const translationKey = `document.grid.default_styles.${selector?.replace('.', '')}`;
    const title = t(translationKey);
    const textStyles = defaultStyles[value].textStyles;
    const { fontSize, fontFamily, color, lineHeight, textAlign } = textStyles;
    const rowBackgroundColor = '--table-row-default-background-color';

    const textStyleComponents = {
      title,
      textStyles: [
        {
          componentTitle: t('document.grid.default_styles.font'),
          components: [
            <StyledComponentInput
              handleChange={(selectChangeEvent) =>
                handleTextDefaultStyleChange(
                  selector,
                  'fontFamily',
                  Array.isArray(selectChangeEvent.target.value) ? selectChangeEvent.target.value[0] : selectChangeEvent.target.value
                )
              }
              value={(fontFamily as string) || DEFAULT_FONT_FAMILY}
              width="144px"
              key={`font-family-${title}`}
              menuItems={allAvailableFonts}
            />,
          ],
        },
        {
          componentTitle: t('document.grid.default_styles.font_size'),
          components: [
            <FontSizeInput
              key={`font-size-${title}`}
              width="50px"
              value={fontSize as string}
              onChange={(value) => handleTextDefaultStyleChange(selector, 'fontSize', value)}
              menuItems={fontSizeOptions}
            />,
          ],
        },
        {
          componentTitle: t('document.grid.default_styles.color'),
          components: [
            <ColorPicker
              key={`color-${title}`}
              currentColor={color as string}
              handleColorChange={(color) => handleTextDefaultStyleChange(selector, 'color', color)}
              onOpen={handleColorPickerOpen}
              presetColors={documentColors}
            />,
          ],
        },
        {
          componentTitle: t('document.grid.default_styles.text_style'),
          components: (
            <TextStyleButtons
              key={`text-style-${title}`}
              onChange={(attributeName, value) => handleTextDefaultStyleChange(selector, attributeName, value)}
              textDefaultStyles={textStyles}
            />
          ),
        },
        {
          componentTitle: t('document.grid.default_styles.line_spacing'),
          components: [
            <StyledComponentInput
              key={`line-spacing-${title}`}
              value={
                DEFAULT_LINE_HEIGHT_OPTIONS.find((lineHeightOption) => lineHeightOption.level === lineHeight)?.name ?? DEFAULT_LINE_HEIGHT
              }
              menuItems={lineHeightMenuItems}
              handleChange={(selectChangeEvent) =>
                handleTextDefaultStyleChange(selector, 'lineHeight', lineHeightMenuItemsVal[selectChangeEvent.target.value as string])
              }
              width="84px"
            />,
          ],
        },
        {
          componentTitle: t('document.grid.default_styles.text_alignment'),
          components: (
            <TextAlignmentButtons
              key={`text-alignment-${title}`}
              onChange={(alignment) => handleTextDefaultStyleChange(selector, 'textAlign', alignment)}
              activeAlignment={textAlign}
            />
          ),
        },
      ],
    };

    const rowStyleComponents = [
      {
        componentTitle: t('document.grid.default_styles.row_body_background_color'),
        components: [
          <ColorPicker
            key={`color-${title}`}
            currentColor={defaultStyles[value].rowStyles?.[rowBackgroundColor] as string}
            handleColorChange={(color) => handleTableRowDefaultStyleChange(tableStyleToRowClassMap[selector], rowBackgroundColor, color)}
            onOpen={handleColorPickerOpen}
            presetColors={documentColors}
          />,
        ],
      },
    ];

    return { ...textStyleComponents, rowStyles: defaultStyles[value].rowStyles ? rowStyleComponents : [] };
  });

  return accordionData;
};

export default useAccordionData;
